import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { OverflowMenu, Tab, vars } from "@pack-it-up/ui-web";
import { copyName, type TemplateSummary } from "@pack-it-up/core";
import { ownedTemplates } from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { ArchiveRow } from "../shared/ArchiveRow.js";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog.js";
import { CopyDialog } from "../shared/CopyDialog.js";
import { PageHeader } from "../shared/PageHeader.js";
import { SearchBox, matchesName, useSearch } from "../shared/SearchBox.js";
import { SectionHead } from "../shared/SectionHead.js";
import { TemplateGrid } from "../shared/TemplateGrid.js";
import { NewTemplateModal } from "../home/NewTemplateModal.js";

// My Templates (issue #163): templates and archived templates in one place — Home only
// shows templates, and there's no longer a combined trips+templates archive page.
export const MyTemplatesPage = (): ReactNode => {
  const { home, archive, copy, pendingTemplateDelete } = useSession();
  const { auth } = useStores();
  const navigate = useNavigate();
  const isAnonymous = useStoreValue(auth, (s) => s.user?.isAnonymous ?? false);

  const rawTemplates = useStoreValue(home, (s) => s.templates);
  const rawArchivedTemplates = useStoreValue(archive, (s) => s.templates);
  const busy = useStoreValue(copy, (s) => s.busy);
  const copyError = useStoreValue(copy, (s) => s.error);
  // The delete write is deferred behind an Undo toast (AppShell), so the underlying
  // lists still have it — hide it here until the write actually lands (`hiddenIds`
  // outlives the toast itself, so nothing flickers back into view while it's in
  // flight). A template can be deleted from either list, so both are filtered.
  const hiddenTemplateIds = useStoreValue(pendingTemplateDelete, (s) => s.hiddenIds);
  const allTemplates = useMemo(
    () => rawTemplates.filter((t) => !hiddenTemplateIds.includes(t.id)),
    [rawTemplates, hiddenTemplateIds],
  );
  const archivedTemplates = useMemo(
    () => rawArchivedTemplates.filter((t) => !hiddenTemplateIds.includes(t.id)),
    [rawArchivedTemplates, hiddenTemplateIds],
  );

  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TemplateSummary | null>(null);
  const [pendingCopy, setPendingCopy] = useState<TemplateSummary | null>(null);
  // Issue #8 — templates shared with you show up in the same list, so the list opens
  // on the ones you made and the toggle widens it.
  const [mineOnly, setMineOnly] = useState(true);

  const templates = mineOnly ? ownedTemplates(allTemplates) : allTemplates;
  const sharedCount = allTemplates.length - ownedTemplates(allTemplates).length;

  // Search filters both sections; the section counts stay whole-list, as on Home.
  const { query, filtered, onChange: handleSearch } = useSearch();
  const search = (list: TemplateSummary[]): TemplateSummary[] =>
    filtered === "" ? list : list.filter((t) => matchesName(t.name, filtered));
  const shownTemplates = search(templates);
  const shownArchived = search(archivedTemplates);

  const openTemplate = (id: string): void => {
    navigate(`/template/${id}`);
  };

  const confirmDelete = async (): Promise<void> => {
    if (pendingDelete === null) return;
    const t = pendingDelete;
    setPendingDelete(null);
    await archive.getState().deleteTemplate(t.id);
  };

  const confirmCopy = async (name: string): Promise<void> => {
    if (pendingCopy === null) return;
    const r = await copy.getState().duplicateTemplate(pendingCopy.id, name);
    if (!r.ok) return; // the dialog stays open showing the error
    setPendingCopy(null);
    navigate(`/template/${r.value}`);
  };

  return (
    <>
      <PageHeader
        title="My Templates"
        right={<SearchBox value={query} onChange={handleSearch} placeholder="Search templates…" />}
      />

      <div style={{ padding: `${vars.space.xl} ${vars.space.xl} calc(${vars.space.xl} * 2)` }}>
        <section>
          <SectionHead title="Templates" count={`${templates.length} saved`} />
          <div role="tablist" aria-label="Template owner filter" style={{ display: "flex", gap: vars.space.md, marginBottom: vars.space.md }}>
            <Tab active={mineOnly} onClick={() => setMineOnly(true)}>
              Mine
            </Tab>
            <Tab active={!mineOnly} onClick={() => setMineOnly(false)} count={sharedCount > 0 ? sharedCount : undefined}>
              All
            </Tab>
          </div>
          {templates.length === 0 ? (
            <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>
              {mineOnly && allTemplates.length > 0 ? "No templates of your own yet." : "No templates yet."}
            </p>
          ) : shownTemplates.length === 0 ? (
            <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>No templates match your search.</p>
          ) : null}
          <TemplateGrid
            templates={shownTemplates}
            onOpen={openTemplate}
            {...(filtered === "" && !isAnonymous ? { onNew: () => setShowNewTemplate(true) } : {})}
            renderAction={(t) => (
              <OverflowMenu
                label={`More options for ${t.name}`}
                items={[{ label: "Duplicate", onClick: () => setPendingCopy(t) }]}
              />
            )}
          />
        </section>

        <section>
          <SectionHead
            title="Archived Templates"
            count={`${archivedTemplates.length} ${archivedTemplates.length === 1 ? "template" : "templates"}`}
          />
          {archivedTemplates.length === 0 ? (
            <p style={{ color: vars.color.textMuted }}>No archived templates.</p>
          ) : shownArchived.length === 0 ? (
            <p style={{ color: vars.color.textMuted }}>No templates match your search.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: vars.space.sm }}>
              {shownArchived.map((t) => (
                <ArchiveRow
                  key={t.id}
                  name={t.name}
                  onOpen={() => openTemplate(t.id)}
                  onRestore={() => void archive.getState().unarchiveTemplate(t.id)}
                  onDelete={() => setPendingDelete(t)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <NewTemplateModal open={showNewTemplate} onClose={() => setShowNewTemplate(false)} />

      <CopyDialog
        open={pendingCopy !== null}
        title="Duplicate template"
        subtitle="Creates your own copy — categories, items and to-dos included."
        label="Template name"
        defaultName={pendingCopy === null ? "" : copyName(pendingCopy.name)}
        confirmLabel="Duplicate"
        busy={busy}
        error={copyError}
        onClose={() => setPendingCopy(null)}
        onConfirm={(name) => void confirmCopy(name)}
      />

      <ConfirmDeleteDialog
        name={pendingDelete?.name ?? null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
